import {
	Children,
	FC,
	isValidElement,
	ReactElement,
	ReactNode,
} from 'react';
import {
	useDispatch,
} from 'react-redux';
// COMPONENT
import {
	Button,
	ButtonProps,
} from '@components/button';
import {
	Icon,
} from '@components/icon';
import {
	EnumButtonSize,
} from '@enums/button.enum';
// ENUMS
import {
	EnumComponentType,
} from '@enums/component.enum';
import {
	EnumFontStyle,
} from '@enums/font.enum';
import {
	EnumStatusTheme,
	EnumTheme,
} from '@enums/theme.enum';
// STORES
import {
	clearStatusMsgs,
} from '@stores/_slices/status_msgs';
// STYLES
import styles from './status-banner.module.scss';

export interface StatusBannerProps {
	'buttons'?: Array<ButtonProps>;
	'children'?: ReactNode;
	'className'?: string;
	'data-testid'?: string;
	'hasIcon'?: boolean;
	'isClosable'?: boolean;
	'message'?: {
		i18nKey: string;
		values?: Record<string, unknown>;
		context?: string;
		list?: {
			i18nKey: string;
			values?: Record<string, unknown>;
			context?: string;
		}[];
	};
	'theme'?: EnumStatusTheme;
}

export const StatusBanner = ({
	children,
	className,
	'data-testid': dataTestid,
	hasIcon = true,
	isClosable = false,
	theme = EnumStatusTheme.INFO,
}: StatusBannerProps): ReactNode | null => {
	const dispatch = useDispatch();

	const cssClasses = [
		styles.banner,
	];

	if (className) cssClasses.push(className);
	if (hasIcon) cssClasses.push(styles.icon);
	if (isClosable) cssClasses.push(styles.closable);
	cssClasses.push(styles[`${'theme__' + theme}`]);

	let iconElement = null;

	let iconName = 'info-circle';
	switch (theme) {
		case EnumStatusTheme.ERROR:
			iconName = 'times-circle';
			break;
		case EnumStatusTheme.SUCCESS:
			iconName = 'check-circle';
			break;
		case EnumStatusTheme.WAIT:
		case EnumStatusTheme.WARNING:
			iconName = 'exclamation-triangle';
			break;
		default:
	}

	iconElement = hasIcon ? (
		<Icon
			className={styles.icon}
			data-testid={`${dataTestid}-icon`}
			fontStyle={EnumFontStyle.SOLID}
			name={iconName}
		/>
	) : null;

	const isButtonElement = (child: ReactElement): boolean => {
		const type = child.type as FC;
		return (type.displayName || type.name) === EnumComponentType.BUTTON;
	};

	const renderChildrenByType = (
		children: ReactNode,
		filter: 'buttons' | 'message',
	): ReactNode[] =>
		Children.toArray(children).filter((child) => {
			if (!isValidElement(child)) return child;
			return filter === 'buttons' ? isButtonElement(child) : !isButtonElement(child);
		});

	const messageElement = (
		<span className={styles.message}>
			{renderChildrenByType(children, 'message')}
		</span>
	);

	const buttonElement = (
		renderChildrenByType(children, 'buttons').length > 0 && (
			<div
				className={styles.buttons}
				data-testid={`${dataTestid}-buttons`}
			>
				{renderChildrenByType(children, 'buttons')}
			</div>
		)
	);

	const handleOnClose = () => {
		dispatch(clearStatusMsgs());
	};

	const closeElement = isClosable ? (
		<Button
			className={styles.closable_button}
			data-testid={`${dataTestid}-close`}
			hasBoxShadow={false}
			iconLeft="times"
			iconStyle={EnumFontStyle.REGULAR}
			onClick={handleOnClose}
			size={EnumButtonSize.SMALL}
			theme={EnumTheme.NAKED}
		/>
	) : null;

	return (
		<div
			aria-live='assertive'
			className={cssClasses.join(' ')}
			data-testid={dataTestid}
			role='alert'
		>
			{closeElement}
			<div className={styles.content}>
				{iconElement}
				{messageElement}
			</div>
			{buttonElement}
		</div>
	);
};
